<template>
  <section id="projects" class="projects">
    <div class="container">
      <div class="section-heading">
        <div class="heading-line"/>
        <h2 class="section-title">Projects</h2>
      </div>
      <p class="section-subtitle">
        A curated collection of my most impactful projects, from dynamic web apps to cutting-edge AI integrations
      </p>

      <div class="projects-grid">
        <!-- Renders as a plain <div> when there's no link: several projects
             have no public URL, and an <a> with no href still carried the
             hover tilt, so those cards looked clickable but did nothing. -->
        <component
          :is="getProjectLink(project) ? 'a' : 'button'"
          v-for="project in displayedProjects"
          :key="project.id"
          :href="getProjectLink(project) || undefined"
          :target="getProjectLink(project) ? '_blank' : undefined"
          :rel="getProjectLink(project) ? 'noopener noreferrer' : undefined"
          class="project-card"
          :class="{ 'is-static': !getProjectLink(project) }"
          :type="getProjectLink(project) ? undefined : 'button'"
          @click="!getProjectLink(project) && (unavailableProject = project)"
        >
          <div class="project-card__image-wrapper">
            <!-- No screenshot yet: show what the project IS rather than a
                 stock photo that implies it's the real interface. The corner
                 marker is skipped here since the glyph already says it. -->
            <template v-if="project.thumbnail">
              <!-- Lazy because the home page is a horizontal track: all six panels
                 are in the DOM from the first byte, so without this every image
                 on every panel is fetched and decoded before anyone has scrolled
                 anywhere. decoding="async" keeps the decode off the main thread,
                 which is what was making the scroll stutter as each panel
                 arrived. -->
              <img
                :src="project.thumbnail"
                :alt="project.title"
                class="project-card__image"
                loading="lazy"
                decoding="async"
              >
              <span
                v-if="glyphName(project)"
                class="project-card__type"
                :title="typeLabel(project.type)"
                :aria-label="typeLabel(project.type)"
              >
                <ProjectGlyph :name="glyphName(project)" :size="16" />
              </span>
            </template>
            <div
              v-else
              class="project-card__placeholder"
              :title="typeLabel(project.type)"
              :aria-label="typeLabel(project.type)"
            >
              <ProjectGlyph :name="glyphName(project)" :size="46" :stroke-width="1.4" />
            </div>
          </div>

          <div class="project-card__content">
            <h3 class="project-card__title">
              <span>{{ project.title }}</span>
              <span v-if="getLinkIconType(project)" class="project-card__icon">
                <svg v-if="getLinkIconType(project) === 'external'" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="7" y1="17" x2="17" y2="7"/>
                  <polyline points="8 7 17 7 17 16"/>
                </svg>
                <svg v-else-if="getLinkIconType(project) === 'github'" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                </svg>
                <img v-else-if="getLinkIconType(project) === 'npm'" src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/npm/npm-original-wordmark.svg" alt="npm" class="project-card__npm-icon" decoding="async">
              </span>
            </h3>
            <p class="project-card__description">{{ project.description }}</p>
          </div>
        </component>

        <NuxtLink to="/projects" class="view-all-card">
          <div class="view-all-card__icon">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
              <line x1="7" y1="17" x2="17" y2="7"/>
              <polyline points="8 7 17 7 17 16"/>
            </svg>
          </div>
          <h3 class="view-all-card__title">Explore All</h3>
          <p class="view-all-card__description">See the complete portfolio</p>
        </NuxtLink>
      </div>
    </div>

    <ProjectUnavailableModal :project="unavailableProject" @close="unavailableProject = null" />
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { getTopProjects } from '~/data/content';

// 7 featured projects + the "Explore All" card = 8 total, laid out as a 4x2 grid.
const displayedProjects = computed(() => getTopProjects(7));

function getProjectLink(project) {
  return project.demoUrl || project.repoUrl || null;
}

const TYPE_LABELS = {
  web: 'Web',
  app: 'App',
  hybrid: 'Hybrid — mobile and software',
};

function typeLabel(type) {
  return TYPE_LABELS[type] || '';
}

// npm packages get the npm mark regardless of link target; otherwise a live
// demo gets an external-link arrow, and a repo-only project gets GitHub's.
// The card artwork/marker says what KIND of project this is: an npm package
// shows npm's mark, a repo-only project shows GitHub's, and anything else
// falls back to its build type (web / app / hybrid).
function glyphName(project) {
  if (project.category === 'package') return 'npm';
  // An explicit build type wins; otherwise anything with source available
  // shows GitHub's mark, and the last resort is the generic web glyph — so
  // no card can ever render blank artwork.
  if (project.type) return project.type;
  if (project.repoUrl) return 'github';
  return 'web';
}

function getLinkIconType(project) {
  if (project.category === 'package') return 'npm';
  if (project.demoUrl) return 'external';
  if (project.repoUrl) return 'github';
  return null;
}

// Cards with no public link are still clickable — they just say so. Silently
// doing nothing reads as a broken card, and these are real projects that simply
// have nothing to link to yet.
const unavailableProject = ref(null);
</script>

<style lang="scss" scoped>
.projects {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 4vh, 3rem) 0;
  background: transparent;
  position: relative;
  overflow: hidden;

  .container {
    width: 100%;
  }

  // Nothing is pinned below this, so `height: 100%` resolves to `auto` and
  // there is no screen left for `justify-content` to centre against. The
  // padding stops being decoration and becomes the whole gap between one
  // section and the next, which 4vh of a phone is not.
  @media (max-width: 768px) {
    padding: clamp(3rem, 9vh, 4.5rem) 0;
  }
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);

  .heading-line {
    // Hidden site-wide. Kept in the markup and styled so it can be brought
    // back by dropping this one line. display:none rather than opacity/width so
    // the flex gap beside it collapses too and the title sits flush left.
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
    // Tapered to a point at both ends and faded out, so it reads as a mark
    // rather than a hard bar. The clip gives the sharp tips; the gradient
    // stops the tips ending abruptly.
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);
  }

  .section-title {
    font-size: clamp(1.5rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

.section-subtitle {
  text-align: left;
  color: var(--muted);
  font-size: clamp(0.8rem, 0.5vw + 0.8vh, 1rem);
  max-width: 620px;
  margin: 0 0 clamp(1rem, 2.5vh, 2.5rem);
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(0.6rem, 1vw + 1vh, 1.5rem);
  perspective: 1200px;
}

// One unified card (background + border wrapping image + text together)
// with a subtle 3D tilt-and-lift on hover.
.project-card {
  // Linkless cards render as <button>, which arrives with its own font, text
  // alignment and border. Reset here so both kinds of card look identical.
  font: inherit;
  text-align: left;
  width: 100%;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  // Opaque: --surface-rgb was never defined, so this used to resolve to an
  // invalid colour and the card rendered fully transparent. Painting the page
  // colour looks the same as before but stops the background line showing
  // through the card.
  background: var(--background);
  border: 1px solid rgba(var(--border-rgb), 0.25);
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transform-style: preserve-3d;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out;

  // Unlinked cards still respond to hover — they just lift, with no tilt or
  // perspective. The 3D transform reads as "click me" and also distorts the
  // card's own header text, which is wrong for something that isn't clickable.
  &.is-static {
    cursor: default;

    &:hover {
      transform: translateY(-6px);
      border-color: rgba(var(--accent-rgb), 0.4);
      box-shadow: 0 18px 35px rgba(var(--accent-rgb), 0.14);
    }
  }

  &:hover:not(.is-static) {
    transform: translateY(-6px) rotateX(4deg) rotateY(-4deg) scale(1.015);
    box-shadow:
      0 24px 45px rgba(var(--accent-rgb), 0.18),
      0 0 0 1px rgba(var(--accent-rgb), 0.12);
    border-color: rgba(var(--accent-rgb), 0.4);
    text-decoration: none;

    .project-card__image {
      transform: scale(1.06);
    }

    .project-card__icon {
      transform: translate(2px, -2px);
    }
  }
}

.project-card__type {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  // Flat translucent fill rather than backdrop-filter: these sit over
  // imagery inside a track that moves on every scroll frame, which is the
  // pathological case for backdrop blur.
  background: rgba(10, 14, 20, 0.55);
  color: #fff;
}

.project-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--accent-rgb), 0.07);
  color: rgba(var(--accent-rgb), 0.75);
}

.project-card__image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  // Caps the thumbnail by viewport height too, not just column width — on a
  // short/wide viewport this wins over the aspect-ratio, keeping 2 rows of
  // cards from overflowing the panel's 100vh.
  max-height: 16vh;
  overflow: hidden;
  flex-shrink: 0;
}

.project-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease-in-out;
}

.project-card__content {
  padding: clamp(0.6rem, 0.5vw + 0.8vh, 1rem);
  display: flex;
  flex-direction: column;
  gap: clamp(0.25rem, 0.6vh, 0.4rem);
  flex-grow: 1;
}

.project-card__title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: clamp(0.9rem, 0.6vw + 0.8vh, 1.1rem);
  font-weight: 700;
  color: var(--text);
  margin: 0;

  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.project-card__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: var(--text);
  opacity: 0.7;
  transition: transform 0.2s ease-in-out;

  .project-card__npm-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
}

.project-card__description {
  color: var(--muted);
  font-size: clamp(0.75rem, 0.3vw + 0.7vh, 0.85rem);
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.view-all-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: clamp(0.3rem, 0.8vh, 0.5rem);
  padding: clamp(0.75rem, 1.2vw + 1.5vh, 1.5rem);
  background: rgba(var(--accent-rgb), 0.05);
  border: 1.5px dashed rgba(var(--accent-rgb), 0.35);
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  transform-style: preserve-3d;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out, background 0.2s ease-in-out;

  &:hover {
    transform: translateY(-6px) rotateX(4deg) rotateY(-4deg) scale(1.015);
    background: rgba(var(--accent-rgb), 0.1);
    border-color: rgba(var(--accent-rgb), 0.6);
    box-shadow:
      0 24px 45px rgba(var(--accent-rgb), 0.18),
      0 0 0 1px rgba(var(--accent-rgb), 0.12);
    text-decoration: none;

    .view-all-card__icon {
      transform: rotate(45deg);
    }
  }
}

.view-all-card__icon {
  color: var(--accent);
  transition: transform 0.2s ease-in-out;
}

.view-all-card__title {
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(0.95rem, 1vw + 0.4vh, 1.1rem);
  font-weight: 700;
  color: var(--text);
  margin: 0;
}

.view-all-card__description {
  color: var(--muted);
  font-size: 0.8rem;
  margin: 0;
}

@media (max-width: 1100px) {
  .projects-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .projects-grid {
    grid-template-columns: 1fr;
  }
}

// Section headings step down noticeably on phones: the desktop clamp is tuned
// for a 100vh panel and reads oversized on a narrow screen.
@media (max-width: 768px) {
  .section-heading {
    gap: 0.6rem;

    .heading-line {
      width: 26px;
      height: 2px;
    }

    .section-title {
      font-size: clamp(0.95rem, 4.2vw, 1.25rem);
      letter-spacing: 2px;
    }
  }
}
</style>
